import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const UserProfile = () => {
  const { user, refreshUser } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailStep, setEmailStep] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameError, setNameError] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    fetchMe();
  }, []);

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setMe(res.data);
    } catch (_) {
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address');
      return;
    }
    setSendingOtp(true);
    try {
      await axios.post(`${API_URL}/auth/send-email-otp`, { email });
      setEmailStep('verify');
      setEmailError('');
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!emailOtp.trim()) {
      setEmailError('Enter the OTP sent to your email');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/auth/verify-email-otp`, { email: newEmail.trim().toLowerCase(), otp: emailOtp.trim() });
      setEmailStep(null);
      setNewEmail('');
      setEmailOtp('');
      setEmailError('');
      await fetchMe();
      if (refreshUser) await refreshUser();
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const displayEmail = (email) => {
    if (!email) return '—';
    if (email.endsWith('@trial') || /^mobile_\d+@trial$/i.test(email)) return 'Not set';
    return email;
  };

  const isEmailNotSet = (email) => !email || email.endsWith('@trial') || /^mobile_\d+@trial$/i.test(email);

  const startEditName = () => {
    setNameValue(me?.name || '');
    setNameError('');
    setEditingName(true);
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameError('');
    const trimmed = nameValue.trim();
    if (!trimmed) { setNameError('Name is required'); return; }
    setSavingName(true);
    try {
      await axios.patch(`${API_URL}/auth/me`, { name: trimmed });
      setMe((prev) => (prev ? { ...prev, name: trimmed } : { name: trimmed }));
      if (refreshUser) await refreshUser();
      setEditingName(false);
    } catch (err) {
      setNameError(err.response?.data?.error || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading profile…</p>
        </div>
      </div>
    );
  }

  const needsEmail = me?.needs_email_verification === true;
  const initials = (me?.name || 'U').trim().split(/\s+/).map((s) => s[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Profile</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your account details</p>
      </div>

      {/* Email verification alert */}
      {needsEmail && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800">
          <svg className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>Add and verify your email address below to secure your account.</span>
        </div>
      )}

      {/* Account card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Account</h2>
        </div>

        {/* Avatar + name row */}
        <div className="px-5 py-4 flex items-center gap-4 border-b border-slate-100">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-violet-700 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {editingName ? (
                <form onSubmit={handleSaveName} className="flex flex-wrap items-center gap-2 w-full">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => { setNameValue(e.target.value); setNameError(''); }}
                    className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-1.5 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                    placeholder="Your name"
                    autoFocus
                  />
                  <button type="submit" disabled={savingName}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50 transition-all"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    {savingName ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => { setEditingName(false); setNameError(''); }}
                    className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <span className="text-sm font-semibold text-slate-900">{me?.name || '—'}</span>
                  <button type="button" onClick={startEditName}
                    className="p-1 rounded-lg text-slate-400 hover:bg-violet-50 hover:text-violet-600 transition-colors"
                    title="Edit name">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </>
              )}
            </div>
            {nameError && <p className="mt-1 text-xs text-rose-600">{nameError}</p>}
          </div>
        </div>

        {/* Phone row */}
        <div className="px-5 py-3.5 flex items-center gap-3 border-b border-slate-100">
          <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 font-medium">Mobile</p>
            <p className="text-sm text-slate-800 font-medium">{me?.phone || '—'}</p>
          </div>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Login ID · cannot change</span>
        </div>

        {/* Email row + update form */}
        <div className="px-5 py-3.5 border-t border-slate-100 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-400 font-medium">Email</p>
              <p className={`text-sm font-medium ${isEmailNotSet(me?.email) ? 'text-slate-400 italic' : 'text-slate-800'}`}>
                {displayEmail(me?.email)}
              </p>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-600 mb-2">
              {isEmailNotSet(me?.email) ? 'Add email address' : 'Update email address'}
            </p>
            {!emailStep ? (
              <form onSubmit={handleSendEmailOtp} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    {isEmailNotSet(me?.email) ? 'Email address' : 'New email address'}
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); setEmailError(''); }}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                    placeholder="you@example.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sendingOtp}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all hover:shadow-md active:scale-95 whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                >
                  {sendingOtp ? 'Sending…' : 'Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyEmailOtp} className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-violet-50 border border-violet-200 rounded-xl text-sm text-violet-800">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  OTP sent to <strong className="mx-1">{newEmail}</strong>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Enter 6-digit OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={emailOtp}
                    onChange={(e) => { setEmailOtp(e.target.value.replace(/\D/g, '')); setEmailError(''); }}
                    className="w-32 rounded-xl border border-slate-200 px-3 py-2.5 text-center text-lg tracking-widest font-mono focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                    placeholder="000000"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setEmailStep(null); setEmailOtp(''); setEmailError(''); }}
                    className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={verifying}
                    className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                  >
                    {verifying ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              </form>
            )}
            {emailError && (
              <p className="mt-2 text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-lg border border-rose-200">{emailError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
